// Native bridge — single entry-point the rest of the app imports from.
//
// Rules:
//   • `isNative()` lets any component conditionally branch to a native API
//     while keeping the web code path intact.
//   • Every function here is safe to call on web — Capacitor plugins no-op
//     off-platform so our code doesn't need `if (isNative())` sprinkled
//     everywhere.
//   • Side-effectful plugins (StatusBar, SplashScreen, PushNotifications
//     listeners) are initialised ONCE by `initNativeShell()` at app boot.
//     Importing other helpers is cheap.
//
// For GPS specifically see `use-gps.js` which transparently swaps the
// browser Geolocation watcher for Capacitor BackgroundGeolocation when
// running natively — the component API is unchanged.

import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { PushNotifications } from '@capacitor/push-notifications';

export function isNative() {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
}

export function nativePlatform() {
  try { return Capacitor.getPlatform(); } catch { return 'web'; }
}

// ----- Haptics ---------------------------------------------------------
// Preferred over navigator.vibrate on native — iOS Safari ignores vibrate
// entirely, and Android Chrome's vibrate stops when the screen locks.
export async function haptic(level = 'light') {
  if (!isNative()) {
    // Web fallback — vibrate API where supported.
    try {
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        const pattern = level === 'heavy' ? [120, 60, 120] : level === 'medium' ? [80, 40, 80] : [40];
        navigator.vibrate(pattern);
      }
    } catch { /* noop */ }
    return;
  }
  try {
    if (level === 'notification-success') {
      await Haptics.notification({ type: NotificationType.Success });
    } else if (level === 'notification-warning') {
      await Haptics.notification({ type: NotificationType.Warning });
    } else {
      const style = level === 'heavy' ? ImpactStyle.Heavy : level === 'medium' ? ImpactStyle.Medium : ImpactStyle.Light;
      await Haptics.impact({ style });
    }
  } catch { /* noop on web */ }
}

// ----- Status bar + splash --------------------------------------------
async function configureStatusBar() {
  if (!isNative()) return;
  try {
    await StatusBar.setStyle({ style: Style.Dark });   // dark bg → light icons
    if (nativePlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: '#0a0a0a' });
    }
    await StatusBar.setOverlaysWebView({ overlay: false });
  } catch { /* noop */ }
}

async function hideSplashWhenReady() {
  if (!isNative()) return;
  // Capacitor auto-hides after the configured launchShowDuration, but we
  // also hide explicitly once React has mounted so users on fast devices
  // don't stare at the splash longer than needed.
  try { await SplashScreen.hide({ fadeOutDuration: 220 }); } catch { /* noop */ }
}

// ----- Push notifications ---------------------------------------------
// We register for the device token on first launch, then upsert it to the
// Supabase `device_tokens` table from the auth-aware layer so the backend
// can target the user with FCM/APNs via a Supabase Edge Function.
async function registerPush(onToken, onNotification) {
  if (!isNative()) return;
  try {
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') return;
    await PushNotifications.register();
    PushNotifications.addListener('registration', (token) => {
      try { onToken?.(token.value); } catch { /* noop */ }
    });
    PushNotifications.addListener('registrationError', () => { /* swallow */ });
    PushNotifications.addListener('pushNotificationReceived', (n) => {
      try { onNotification?.(n, 'foreground'); } catch { /* noop */ }
    });
    PushNotifications.addListener('pushNotificationActionPerformed', (a) => {
      try { onNotification?.(a.notification, 'tap'); } catch { /* noop */ }
    });
  } catch { /* noop */ }
}

// ----- Boot init ------------------------------------------------------
// Call this once from App.js at mount. Idempotent.
let initDone = false;
export async function initNativeShell({ onPushToken, onPushNotification } = {}) {
  if (initDone || !isNative()) { initDone = true; return; }
  initDone = true;
  await configureStatusBar();
  await hideSplashWhenReady();
  if (onPushToken || onPushNotification) {
    await registerPush(onPushToken, onPushNotification);
  }
}

// Exposed only for callers that want to register push after login (common
// pattern: don't ask for push permission on first-ever launch, wait until
// the user signs in).
export { registerPush };
